<template>
  <PageContainer class="overflow-auto">
    <a-breadcrumb class="mb-[20px]" :separator="separator">
      <a-breadcrumb-item>
        <nuxt-link :to="{ name: PageKey.CENTER_ACCOUNT }">
          {{ $t('zhang-hao-she-zhi') }}
        </nuxt-link>
      </a-breadcrumb-item>
      <a-breadcrumb-item>
        {{ $t('tian-xie-ren-zheng-xin-xi') }}
      </a-breadcrumb-item>
    </a-breadcrumb>
    <div>
      <Banner />
    </div>
    <a-alert
      v-if="verifyInfo?.identify_info?.status === USER_VERIFY_STATUS.CANCEL"
      type="warning"
      showIcon
      class="mt-[20px]"
    >
      <template #message>
        {{ $t('nin-shang-ci-ren-zheng-yi-che-xiao-qing-zhong-xin-ren-zheng') }}
      </template>
    </a-alert>
    <div
      v-if="
        verifyInfo?.identify_info?.status === USER_VERIFY_STATUS.FAIL &&
        current === 0
      "
      class="bg-back-2 p-[20px_86px] mt-[30px] rounded-[12px]"
    >
      <div class="text-16 font-medium mb-[10px] text-font-1">
        {{ $t('shi-bai-yuan-yin') }}：
      </div>
      <Reason
        class="ml-[20px]"
        :reason="verifyInfo?.identify_info?.reject_reason"
      />
    </div>
    <div
      v-if="
        verifyInfo?.identify_info?.status === USER_VERIFY_STATUS.WAIT ||
        !isReadOnly
      "
      class="w-[600px] mx-auto mt-[46px]"
    >
      <XSteps :current="current" :items="items" />
    </div>
    <VerifyForm
      v-if="current === USER_VERIFY_STEP.VERIFY"
      :data="verifyInfo"
      :disabled="isReadOnly"
      @reset="isReadOnly = false"
      @set="changeStep"
      @reload="reload"
    />
    <a-spin v-else :spinning="loading">
      <ResultInfo :data="verifyInfo" @set="changeStep" />
    </a-spin>
  </PageContainer>
</template>

<script lang="ts" setup>
import Banner from './components/banner.vue'
import VerifyForm from './components/form.vue'
import ResultInfo from './components/result.vue'
import Reason from './components/reason.vue'
import type { UserVerifyInfo } from '~/api/interface'
import { fetchVerifyDraft, getVerifyInfo } from '~/api'
import { USER_VERIFY_STATUS, USER_VERIFY_STEP } from '~/constants'
import { PageKey } from '~/constants/page'

const separator = h('iconpark-icon', { name: 'breadcrumb-separator', size: 10 })

const route = useRoute()

const current = ref(
  route.name === PageKey.ACCOUNT_VERIFY
    ? USER_VERIFY_STEP.VERIFY
    : USER_VERIFY_STEP.RESULT
)
const verifyInfo = ref<UserVerifyInfo>()
const isReadOnly = ref(false)
const loading = ref(false)
const { $t } = useNuxtApp()

const items = ref([
  {
    title: '01',
    description: $t('tian-xie-ren-zheng-xin-xi'),
  },
  {
    title: '02',
    description: $t('ping-tai-shen-he'),
  },
  {
    title: '03',
    description: $t('ren-zheng-jie-guo'),
  },
])

const changeStep = (val: any) => {
  current.value = val
}

const reload = async () => {
  await useUpdateUser()
  fetchVerify()
}

const fetchVerify = async () => {
  try {
    loading.value = true
    const res = await getVerifyInfo()
    if (!res.code && res.data) {
      if (!res.data.is_identify && !res.data.identify_info) {
        fetchDraftInfo()
      } else {
        verifyInfo.value = res.data
        isReadOnly.value =
          res.data.identify_info?.status === USER_VERIFY_STATUS.SUCCESS ||
          res.data.identify_info?.status === USER_VERIFY_STATUS.WAIT

        if (res.data.identify_info?.status === USER_VERIFY_STATUS.WAIT) {
          current.value = USER_VERIFY_STEP.WAIT
        }

        if (res.data.identify_info?.status === USER_VERIFY_STATUS.CANCEL) {
          current.value = USER_VERIFY_STEP.VERIFY
        }
      }
      loading.value = false
    }
  } catch (e) {
    console.error(e)

    loading.value = false
  }
}

const fetchDraftInfo = async () => {
  const res = await fetchVerifyDraft()
  if (!res.code && res.data) {
    verifyInfo.value = {
      is_identify: false,
      identify_info: res.data?.[0],
    }
  }
}

onMounted(() => {
  fetchVerify()
})

onMounted(() => {
  emitter.on(EVENT.UPDATE_USER, () => {
    current.value = USER_VERIFY_STEP.RESULT
    fetchVerify()
  })
})
</script>
